const article = {
  slug: "methodology",
  cluster: "data",
  title: "Listino Methodology & Data Sources: How We Prepare Listings",
  metaTitle: "Methodology & Data Sources | Listino",
  description:
    "How Listino produces a listing analysis, and the data sources behind the figures and rules of thumb in our guides.",
  audience: "Sellers & agents",
  funnelStage: "BOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "5 min read",
  tldr: "Every Listino listing prep combines the property details you provide, comparable properties found through current public web research, and established real estate research. Agents should verify time-sensitive facts and pricing before using them with a client.",
  blocks: [
    {
      type: "h2",
      id: "what-its-based-on",
      text: "What our analysis is based on",
    },
    {
      type: "p",
      spans: [
        "A Listino listing prep combines three things: (1) the property details you provide or import, (2) comparable properties identified through current publicly accessible web sources, and (3) established real estate research about what moves listings. It is research assistance, not an MLS feed or appraisal; verify time-sensitive facts before client use.",
      ],
    },
    {
      type: "h2",
      id: "primary-sources",
      text: "Our primary sources",
    },
    {
      type: "p",
      spans: [
        "When our guides reference market behavior, the underlying data generally comes from sources like these:",
      ],
    },
    {
      type: "ul",
      items: [
        [
          {
            text: "NAR Profile of Home Buyers and Sellers",
            href: "https://www.nar.realtor/research-and-statistics",
          },
          " and other National Association of Realtors research — buyer behavior, search habits, and seller outcomes.",
        ],
        [
          { text: "Zillow Research", href: "https://www.zillow.com/research/" },
          " — listing engagement, pricing, and days-on-market trends.",
        ],
        [
          {
            text: "Remodeling's Cost vs. Value Report",
            href: "https://www.costvsvalue.com",
          },
          " — return-on-investment ranges for common pre-sale improvements (these change yearly, so we date them).",
        ],
        "Publicly accessible listing, brokerage, assessor, and other web sources — used to identify candidate comparables, with source details retained where available.",
        "First-party data from listings analyzed on Listino — used only in clearly-labeled, aggregated form.",
      ],
    },
    {
      type: "h2",
      id: "how-we-handle-stats",
      text: "How we handle statistics in our guides",
    },
    {
      type: "ul",
      items: [
        "We prefer qualitative, durable statements over precise figures that go stale.",
        "When we cite a number, we attribute it to a primary source and date it.",
        "Worked numeric examples are labeled as illustrative examples, not as data or guarantees.",
        "Figures that move year to year (such as remodeling ROI) are refreshed on a regular cadence.",
      ],
    },
    {
      type: "callout",
      spans: [
        "If you ever find a figure on this site that looks out of date or unsourced, email contact@listino.ai and we'll correct it.",
      ],
    },
    {
      type: "h2",
      id: "how-a-listing-prep-is-produced",
      text: "How a listing prep is produced",
    },
    {
      type: "ol",
      items: [
        [
          "You ",
          { text: "import a Zillow listing or enter property details", href: "/get-started" },
          ".",
        ],
        "We research candidate comparable sales and active competition using publicly accessible web sources.",
        "We analyze market competitiveness, listing appeal, and price position.",
        "We generate an optimized listing description, a photo sequencing plan, and AI photo touch-ups.",
        "We assemble pricing guidance and projected outcomes into an editable first draft. You can verify facts, revise or restore sections, personalize branding, and publish the finished package.",
      ],
    },
    {
      type: "p",
      spans: [
        "We're also preparing an aggregate data study of the listings we've analyzed; it will be published here once finalized.",
      ],
    },
  ],
  faqs: [
    {
      question: "Does Listino guarantee a faster sale or a specific price?",
      answer: [
        {
          type: "p",
          spans: [
            "No. Our recommendations are grounded in established research and current property research, but market outcomes depend on many factors outside any listing prep. We focus on giving you specific, defensible actions — what to price at, what to fix, and how to present the home.",
          ],
        },
      ],
    },
    {
      question: "Where do the comparable sales come from?",
      answer: [
        {
          type: "p",
          spans: [
            "Listino identifies candidate comparables through current publicly accessible web sources. It does not connect to a private MLS feed. The listing prep includes available source details so an agent can verify the properties, facts, and pricing against authoritative local systems before client use.",
          ],
        },
      ],
    },
  ],
  related: ["is-my-house-overpriced", "what-is-a-cma", "why-isnt-my-house-selling"],
};

export default article;
